import { useEffect, useMemo, useState } from "react";
import { applyHead } from "./head";
import { headForPath } from "./seo-meta";
import {
  ArrowRight,
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  FileText,
  Globe2,
  Mail,
  Menu,
  MessageSquare,
  Newspaper,
  Play,
  Quote,
  ShieldCheck,
  UsersRound,
  X,
} from "lucide-react";
import {
  Actualite,
  actualites,
  Article,
  articles,
  Focus,
  focus,
  events,
  manifestoSections,
  SITE,
  values,
  videos,
  VideoKind,
} from "./content";

type Page =
  | "home"
  | "manifesto"
  | "articles"
  | "article"
  | "actualites"
  | "actualite"
  | "focus"
  | "focus-detail"
  | "videos"
  | "about"
  | "legal"
  | "privacy"
  | "not-found";

type Route = {
  page: Page;
  slug?: string;
};

type LocationState = {
  path: string;
  hash: string;
};

const navItems = [
  { href: "/", label: "Accueil" },
  { href: "/manifeste", label: "Manifeste" },
  { href: "/articles", label: "Articles" },
  { href: "/actualites", label: "Actualités" },
  { href: "/focus", label: "Focus" },
  { href: "/capsules", label: "Capsules" },
  { href: "/qui-sommes-nous", label: "Qui sommes-nous" },
];

const articleCategories = ["Tout", "Diplomatie", "Société", "Économie", "Démocratie", "Santé"] as const;
const videoKinds = ["Tout", "Capsule", "Entretien", "Discours"] as const;

function parseRoute(path: string): Route {
  const cleanPath = path.replace(/\/+$/, "") || "/";

  if (cleanPath === "/") return { page: "home" };
  if (cleanPath === "/manifeste") return { page: "manifesto" };
  if (cleanPath === "/articles") return { page: "articles" };
  if (cleanPath.startsWith("/articles/")) {
    return { page: "article", slug: cleanPath.replace("/articles/", "") };
  }
  if (cleanPath === "/focus") return { page: "focus" };
  if (cleanPath.startsWith("/focus/")) {
    return { page: "focus-detail", slug: cleanPath.replace("/focus/", "") };
  }
  if (cleanPath === "/actualites") return { page: "actualites" };
  if (cleanPath.startsWith("/actualites/")) {
    return { page: "actualite", slug: cleanPath.replace("/actualites/", "") };
  }
  if (cleanPath === "/capsules" || cleanPath === "/videos") return { page: "videos" };
  if (cleanPath === "/qui-sommes-nous") return { page: "about" };
  if (cleanPath === "/mentions-legales") return { page: "legal" };
  if (cleanPath === "/confidentialite") return { page: "privacy" };

  return { page: "not-found" };
}

function getLocationState(): LocationState {
  return {
    path: window.location.pathname,
    hash: window.location.hash,
  };
}

export default function App() {
  const [location, setLocation] = useState<LocationState>(() => getLocationState());
  const [menuOpen, setMenuOpen] = useState(false);
  const route = useMemo(() => parseRoute(location.path), [location.path]);

  useEffect(() => {
    const onPopState = () => setLocation(getLocationState());
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    applyHead(headForPath(location.path));
  }, [location.path]);

  useEffect(() => {
    setMenuOpen(false);
    if (location.hash) {
      window.setTimeout(() => {
        document.querySelector(location.hash)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 60);
      return;
    }
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [location.path, location.hash]);

  function navigate(href: string) {
    const url = new URL(href, window.location.origin);
    window.history.pushState({}, "", `${url.pathname}${url.hash}`);
    setLocation(getLocationState());
  }

  return (
    <div className="app">
      <SkipLink />
      <Header
        activePath={location.path}
        menuOpen={menuOpen}
        onToggleMenu={() => setMenuOpen((value) => !value)}
        onNavigate={navigate}
      />
      <main id="contenu">
        <PageSwitch route={route} onNavigate={navigate} />
      </main>
      <Footer onNavigate={navigate} />
    </div>
  );
}

function SmartLink({
  href,
  className,
  children,
  onNavigate,
  onClick,
  ariaLabel,
}: {
  href: string;
  className?: string;
  children: React.ReactNode;
  onNavigate: (href: string) => void;
  onClick?: () => void;
  ariaLabel?: string;
}) {
  function handleClick(event: React.MouseEvent<HTMLAnchorElement>) {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.altKey ||
      event.ctrlKey ||
      event.shiftKey ||
      href.startsWith("mailto:")
    ) {
      return;
    }

    const url = new URL(href, window.location.origin);
    if (url.origin !== window.location.origin) return;

    event.preventDefault();
    onClick?.();
    onNavigate(href);
  }

  return (
    <a href={href} className={className} onClick={handleClick} aria-label={ariaLabel}>
      {children}
    </a>
  );
}

function SkipLink() {
  return (
    <a href="#contenu" className="skip-link">
      Aller au contenu
    </a>
  );
}

function Header({
  activePath,
  menuOpen,
  onToggleMenu,
  onNavigate,
}: {
  activePath: string;
  menuOpen: boolean;
  onToggleMenu: () => void;
  onNavigate: (href: string) => void;
}) {
  return (
    <header className="site-header">
      <div className="top-ribbon" aria-hidden="true" />
      <div className="container header-row">
        <SmartLink href="/" className="brand" onNavigate={onNavigate}>
          <span className="brand-text">
            Ma voix <strong>pour</strong> de Villepin
          </span>
        </SmartLink>

        <nav className="desktop-nav" aria-label="Navigation principale">
          {navItems.map((item) => (
            <SmartLink
              key={item.href}
              href={item.href}
              className={isActive(activePath, item.href) ? "nav-link active" : "nav-link"}
              onNavigate={onNavigate}
            >
              {item.label}
            </SmartLink>
          ))}
        </nav>

        <div className="header-actions">
          <SmartLink href="/articles" className="btn btn-ghost header-cta" onNavigate={onNavigate}>
            <Newspaper size={18} aria-hidden="true" />
            Lire
          </SmartLink>
          <button
            className="icon-button mobile-menu-button"
            type="button"
            aria-label={menuOpen ? "Fermer le menu" : "Ouvrir le menu"}
            aria-expanded={menuOpen}
            aria-controls="menu-mobile"
            onClick={onToggleMenu}
          >
            {menuOpen ? <X size={22} aria-hidden="true" /> : <Menu size={22} aria-hidden="true" />}
          </button>
        </div>
      </div>

      {menuOpen ? (
        <nav id="menu-mobile" className="mobile-nav container" aria-label="Navigation mobile">
          {navItems.map((item) => (
            <SmartLink
              key={item.href}
              href={item.href}
              className={isActive(activePath, item.href) ? "mobile-nav-link active" : "mobile-nav-link"}
              onNavigate={onNavigate}
            >
              {item.label}
            </SmartLink>
          ))}
          <SmartLink href={`mailto:${SITE.email}`} className="btn btn-primary mobile-mail" onNavigate={onNavigate}>
            <Mail size={18} aria-hidden="true" />
            Nous écrire
          </SmartLink>
        </nav>
      ) : null}
    </header>
  );
}

function isActive(path: string, href: string) {
  if (href === "/") return path === "/";
  if (href === "/articles") return path === "/articles" || path.startsWith("/articles/");
  if (href === "/actualites") return path === "/actualites" || path.startsWith("/actualites/");
  if (href === "/focus") return path === "/focus" || path.startsWith("/focus/");
  if (href === "/capsules") return path === "/capsules" || path === "/videos";
  return path === href;
}

function PageSwitch({ route, onNavigate }: { route: Route; onNavigate: (href: string) => void }) {
  if (route.page === "home") return <HomePage onNavigate={onNavigate} />;
  if (route.page === "manifesto") return <ManifestoPage onNavigate={onNavigate} />;
  if (route.page === "articles") return <ArticlesPage onNavigate={onNavigate} />;
  if (route.page === "article") return <ArticlePage slug={route.slug} onNavigate={onNavigate} />;
  if (route.page === "actualites") return <ActualitesPage onNavigate={onNavigate} />;
  if (route.page === "actualite") return <ActualitePage slug={route.slug} onNavigate={onNavigate} />;
  if (route.page === "focus") return <FocusPage onNavigate={onNavigate} />;
  if (route.page === "focus-detail") return <FocusDetailPage slug={route.slug} onNavigate={onNavigate} />;
  if (route.page === "videos") return <VideosPage onNavigate={onNavigate} />;
  if (route.page === "about") return <AboutPage onNavigate={onNavigate} />;
  if (route.page === "legal") return <LegalPage />;
  if (route.page === "privacy") return <PrivacyPage />;
  return <NotFoundPage onNavigate={onNavigate} />;
}

function HomePage({ onNavigate }: { onNavigate: (href: string) => void }) {
  const featured = articles[0];

  return (
    <>
      <section className="hero" aria-labelledby="hero-title">
        <img className="hero-media" src={SITE.portraitUrl} alt="Portrait officiel de Dominique de Villepin en 2005" />
        <div className="hero-shade" aria-hidden="true" />
        <div className="container hero-content">
          <p className="eyebrow light">{SITE.tagline}</p>
          <h1 id="hero-title">Ma voix pour de Villepin</h1>
          <p className="hero-lead">
            Des citoyens qui pensent, publient et se rassemblent pour que la voix de la France porte à
            nouveau: libre, juste, indépendante.
          </p>
          <div className="hero-actions">
            <SmartLink href="/manifeste" className="btn btn-primary" onNavigate={onNavigate}>
              <FileText size={18} aria-hidden="true" />
              Lire le manifeste
            </SmartLink>
            <SmartLink href="/capsules" className="btn btn-light" onNavigate={onNavigate}>
              <Play size={18} aria-hidden="true" />
              Voir les capsules
            </SmartLink>
          </div>
          <ul className="hero-proof" aria-label="Repères du collectif">
            <li>
              <CheckCircle2 size={18} aria-hidden="true" />
              Tribunes signées et relues
            </li>
            <li>
              <CheckCircle2 size={18} aria-hidden="true" />
              Agenda de rencontres citoyennes
            </li>
            <li>
              <CheckCircle2 size={18} aria-hidden="true" />
              Non affilié à un parti
            </li>
          </ul>
          <p className="image-credit">{SITE.portraitCredit}</p>
        </div>
      </section>

      <section className="status-band" aria-label="État du site">
        <div className="container status-grid">
          <Metric value="5" label="réflexions publiées" />
          <Metric value="6" label="capsules répertoriées" />
          <Metric value="3" label="rendez-vous à venir" />
          <Metric value="48 h" label="réponse presse annoncée" />
        </div>
      </section>

      <section className="section" aria-labelledby="featured-title">
        <div className="container">
          <SectionHeader
            eyebrow="À la une"
            title="Du fond, pas du bruit."
            text="Un format éditorial clair: un article de tête, des analyses par thème et des passerelles vers les capsules."
            action={
              <SmartLink href="/articles" className="text-link" onNavigate={onNavigate}>
                Toutes les réflexions
                <ArrowRight size={17} aria-hidden="true" />
              </SmartLink>
            }
          />
          <article className="lead-article">
            <ArticleVisual article={featured} large />
            <div className="lead-article-body">
              <p className="kicker">
                {featured.kicker} - {featured.category}
              </p>
              <h3 id="featured-title">{featured.title}</h3>
              <p>{featured.dek}</p>
              <div className="meta-row">
                <span>{featured.date}</span>
                <span>{featured.readTime}</span>
              </div>
              <SmartLink href={`/articles/${featured.slug}`} className="btn btn-secondary" onNavigate={onNavigate}>
                <FileText size={18} aria-hidden="true" />
                Lire l'article
              </SmartLink>
            </div>
          </article>
          <div className="article-grid compact-grid">
            {articles.slice(1, 4).map((article) => (
              <ArticleCard key={article.slug} article={article} onNavigate={onNavigate} />
            ))}
          </div>
        </div>
      </section>

      <section className="section section-alt" id="agenda" aria-labelledby="agenda-title">
        <div className="container split-layout">
          <div>
            <p className="eyebrow">Capsule vedette</p>
            <h2 id="agenda-title">La parole en format court.</h2>
            <p className="section-copy">
              Les capsules rassemblent les entretiens, discours et formats courts qui prolongent les
              réflexions publiées.
            </p>
            <VideoFeature onNavigate={onNavigate} />
          </div>
          <AgendaBlock />
        </div>
      </section>

      <ManifestoCta onNavigate={onNavigate} />
    </>
  );
}

function Metric({ value, label }: { value: string; label: string }) {
  return (
    <div className="metric">
      <strong>{value}</strong>
      <span>{label}</span>
    </div>
  );
}

function SectionHeader({
  eyebrow,
  title,
  text,
  action,
}: {
  eyebrow: string;
  title: string;
  text: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="section-header">
      <div>
        <p className="eyebrow">{eyebrow}</p>
        <h2>{title}</h2>
      </div>
      <div className="section-header-side">
        <p>{text}</p>
        {action}
      </div>
    </div>
  );
}

function ArticleVisual({ article, large = false }: { article: Article; large?: boolean }) {
  return (
    <div
      className={`article-visual visual-${article.category.toLowerCase()} ${large ? "large" : ""} ${
        article.image ? "has-image" : ""
      }`}
    >
      {article.image ? (
        <img className="article-visual-img" src={article.image} alt={article.imageAlt ?? ""} loading="lazy" />
      ) : null}
      <span>{article.category}</span>
    </div>
  );
}

function ArticleCard({ article, onNavigate }: { article: Article; onNavigate: (href: string) => void }) {
  return (
    <article className="article-card">
      <ArticleVisual article={article} />
      <div className="article-card-body">
        <p className="kicker">
          {article.kicker} - {article.category}
        </p>
        <h3>
          <SmartLink href={`/articles/${article.slug}`} onNavigate={onNavigate}>
            {article.title}
          </SmartLink>
        </h3>
        <p>{article.dek}</p>
        <div className="meta-row">
          <span>{article.date}</span>
          <span>{article.readTime}</span>
        </div>
      </div>
    </article>
  );
}

function VideoFeature({ onNavigate }: { onNavigate: (href: string) => void }) {
  const video = videos[0];

  const contenu = (
    <>
      <div className="video-stage">
        <span className="play-button" aria-hidden="true">
          <Play size={30} fill="currentColor" />
        </span>
        <div>
          <p>{video.kind}</p>
          <h3>{video.title}</h3>
        </div>
      </div>
      <div className="video-feature-meta">
        <span>{video.duration}</span>
        <span>{video.platform}</span>
      </div>
    </>
  );

  if (video.url) {
    return (
      <a className="video-feature" href={video.url} target="_blank" rel="noopener">
        {contenu}
      </a>
    );
  }

  return (
    <SmartLink href="/capsules" className="video-feature" onNavigate={onNavigate}>
      {contenu}
    </SmartLink>
  );
}

function AgendaBlock() {
  return (
    <aside className="agenda-block" aria-label="Prochains rendez-vous">
      <div className="agenda-title-row">
        <CalendarDays size={22} aria-hidden="true" />
        <h3>Agenda</h3>
      </div>
      <div className="agenda-list">
        {events.map((event) => (
          <div key={`${event.day}-${event.place}`} className="event-row">
            <time>
              <strong>{event.day}</strong>
              <span>{event.month}</span>
            </time>
            <div>
              <h4>{event.title}</h4>
              <p>{event.place}</p>
            </div>
          </div>
        ))}
      </div>
    </aside>
  );
}

function ManifestoCta({ onNavigate }: { onNavigate: (href: string) => void }) {
  return (
    <section className="manifesto-cta" aria-labelledby="manifesto-cta-title">
      <div className="container manifesto-cta-inner">
        <Quote size={34} aria-hidden="true" />
        <p id="manifesto-cta-title">
          Nous nous engageons pour que la voix de la France, libre, juste et indépendante, porte à
          nouveau.
        </p>
        <SmartLink href="/manifeste" className="btn btn-light" onNavigate={onNavigate}>
          <FileText size={18} aria-hidden="true" />
          Le manifeste complet
        </SmartLink>
      </div>
    </section>
  );
}

function ManifestoPage({ onNavigate }: { onNavigate: (href: string) => void }) {
  return (
    <>
      <PageIntro
        eyebrow="Texte fondateur"
        title="Le manifeste"
        text="Nous, citoyennes et citoyens de tous horizons, nous engageons pour que la voix de la France porte à nouveau."
      />
      <section className="section section-tight">
        <div className="container manifesto-list">
          {manifestoSections.map((section) => (
            <article key={section.number} className="manifesto-item">
              <span>{section.number}</span>
              <div>
                <h2>{section.title}</h2>
                <p>{section.text}</p>
              </div>
            </article>
          ))}
        </div>
      </section>
      <section className="section section-alt">
        <div className="container action-panel">
          <div>
            <p className="eyebrow">Prendre part</p>
            <h2>La voix de la France, c'est d'abord la vôtre.</h2>
          </div>
          <div className="action-panel-buttons">
            <SmartLink href="/articles" className="btn btn-secondary" onNavigate={onNavigate}>
              <Newspaper size={18} aria-hidden="true" />
              Lire les réflexions
            </SmartLink>
            <SmartLink href="/qui-sommes-nous#contact" className="btn btn-ghost" onNavigate={onNavigate}>
              <Mail size={18} aria-hidden="true" />
              Écrire au collectif
            </SmartLink>
          </div>
        </div>
      </section>
    </>
  );
}

function ArticlesPage({ onNavigate }: { onNavigate: (href: string) => void }) {
  const [category, setCategory] = useState<(typeof articleCategories)[number]>("Tout");
  const visibleArticles =
    category === "Tout" ? articles : articles.filter((article) => article.category === category);

  return (
    <>
      <PageIntro
        eyebrow="Articles et réflexions"
        title="La revue du collectif"
        text="Tribunes et analyses de fond: diplomatie, souveraineté, justice sociale et renouveau démocratique."
      />
      <section className="section section-tight">
        <div className="container">
          <SegmentedControl
            label="Filtrer les articles"
            values={articleCategories}
            value={category}
            onChange={setCategory}
          />
          <div className="article-grid article-page-grid">
            {visibleArticles.map((article) => (
              <ArticleCard key={article.slug} article={article} onNavigate={onNavigate} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

function ArticlePage({
  slug,
  onNavigate,
}: {
  slug?: string;
  onNavigate: (href: string) => void;
}) {
  const article = articles.find((item) => item.slug === slug);
  const related = articles.filter((item) => item.slug !== slug).slice(0, 3);

  if (!article) return <NotFoundPage onNavigate={onNavigate} />;

  return (
    <>
      <article className="article-page">
        <div className="container article-head">
          <nav className="breadcrumb" aria-label="Fil d'Ariane">
            <SmartLink href="/" onNavigate={onNavigate}>
              Accueil
            </SmartLink>
            <ChevronRight size={15} aria-hidden="true" />
            <SmartLink href="/articles" onNavigate={onNavigate}>
              Articles
            </SmartLink>
          </nav>
          <p className="kicker">
            {article.kicker} - {article.category}
          </p>
          <h1>{article.title}</h1>
          <p className="article-dek">{article.dek}</p>
          <div className="article-meta">
            <span>Par le collectif éditorial</span>
            <time dateTime={article.dateIso}>{article.date}</time>
            <span>{article.readTime} de lecture</span>
          </div>
        </div>
        <div className="container article-hero-visual">
          <ArticleVisual article={article} large />
          {article.imageCredit ? <p className="article-visual-credit">{article.imageCredit}</p> : null}
        </div>
        <div className="article-body">
          {article.body.map((paragraph, index) => (
            <p key={paragraph} className={index === 0 ? "dropcap" : undefined}>
              {paragraph}
            </p>
          ))}
          {article.quote ? <blockquote>{article.quote}</blockquote> : null}
          <div className="tag-list" aria-label="Thèmes de l'article">
            {article.tags.map((tag) => (
              <SmartLink key={tag} href="/articles" className="tag" onNavigate={onNavigate}>
                {tag}
              </SmartLink>
            ))}
          </div>
        </div>
      </article>
      <section className="section section-alt">
        <div className="container">
          <SectionHeader
            eyebrow="À lire ensuite"
            title="Poursuivre le fil."
            text="Quelques textes qui prolongent cette réflexion, dans un format court et lisible."
          />
          <div className="article-grid compact-grid">
            {related.map((item) => (
              <ArticleCard key={item.slug} article={item} onNavigate={onNavigate} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}


function ActualiteCard({
  actualite,
  onNavigate,
}: {
  actualite: Actualite;
  onNavigate: (href: string) => void;
}) {
  return (
    <article className="actualite-card">
      <p className="actualite-date">
        <time dateTime={actualite.dateIso}>{actualite.date}</time>
      </p>
      <h2>
        <SmartLink href={`/actualites/${actualite.slug}`} onNavigate={onNavigate}>
          {actualite.title}
        </SmartLink>
      </h2>
      <p>{actualite.chapeau}</p>
      <SmartLink href={`/actualites/${actualite.slug}`} className="text-link" onNavigate={onNavigate}>
        Lire la brève
        <ArrowRight size={16} aria-hidden="true" />
      </SmartLink>
    </article>
  );
}

function ActualitesPage({ onNavigate }: { onNavigate: (href: string) => void }) {
  return (
    <>
      <PageIntro
        eyebrow="Actualités"
        title="L'actualité, suivie et sourcée"
        text="Chaque fait est daté, recoupé et accompagné de ses sources. Le collectif informe, il ne fait pas campagne."
      />
      <section className="section section-tight">
        <div className="container">
          <div className="actualite-list">
            {actualites.map((actualite) => (
              <ActualiteCard key={actualite.slug} actualite={actualite} onNavigate={onNavigate} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

function ActualitePage({
  slug,
  onNavigate,
}: {
  slug?: string;
  onNavigate: (href: string) => void;
}) {
  const actualite = actualites.find((item) => item.slug === slug);

  if (!actualite) return <NotFoundPage onNavigate={onNavigate} />;

  return (
    <article className="article-page">
      <div className="container article-head">
        <nav className="breadcrumb" aria-label="Fil d'Ariane">
          <SmartLink href="/" onNavigate={onNavigate}>
            Accueil
          </SmartLink>
          <ChevronRight size={15} aria-hidden="true" />
          <SmartLink href="/actualites" onNavigate={onNavigate}>
            Actualités
          </SmartLink>
        </nav>
        <p className="kicker">Actualité</p>
        <h1>{actualite.title}</h1>
        <p className="article-dek">{actualite.chapeau}</p>
        <div className="article-meta">
          <time dateTime={actualite.dateIso}>{actualite.date}</time>
        </div>
      </div>
      <div className="article-body">
        {actualite.corps.map((paragraph, index) => (
          <p key={paragraph} className={index === 0 ? "dropcap" : undefined}>
            {paragraph}
          </p>
        ))}
        <aside className="actualite-sources" aria-label="Sources">
          <h2>Sources</h2>
          <ul>
            {actualite.sources.map((source) => (
              <li key={source.url}>
                <a href={source.url} target="_blank" rel="noopener">
                  {source.label}
                </a>
              </li>
            ))}
          </ul>
        </aside>
        <p className="actualite-back">
          <SmartLink href="/actualites" className="text-link" onNavigate={onNavigate}>
            Toutes les actualités
            <ArrowRight size={16} aria-hidden="true" />
          </SmartLink>
        </p>
      </div>
    </article>
  );
}


function FocusCard({ dossier, onNavigate }: { dossier: Focus; onNavigate: (href: string) => void }) {
  return (
    <article className="focus-card">
      <p className="focus-date">
        <time dateTime={dossier.dateIso}>{dossier.date}</time>
      </p>
      <h2>
        <SmartLink href={`/focus/${dossier.slug}`} onNavigate={onNavigate}>
          {dossier.title}
        </SmartLink>
      </h2>
      <p className="focus-idea">{dossier.idea}</p>
      <p className="focus-chapeau">{dossier.chapeau}</p>
      <SmartLink href={`/focus/${dossier.slug}`} className="text-link" onNavigate={onNavigate}>
        Ouvrir le dossier
        <ArrowRight size={16} aria-hidden="true" />
      </SmartLink>
    </article>
  );
}

function FocusPage({ onNavigate }: { onNavigate: (href: string) => void }) {
  return (
    <>
      <PageIntro
        eyebrow="Focus"
        title="Les idées, passées au crible"
        text="Un dossier par idée défendue par Dominique de Villepin, confrontée à ce qu'en disent la presse française et la presse étrangère."
      />
      <section className="section section-tight">
        <div className="container">
          <div className="focus-list">
            {focus.map((dossier) => (
              <FocusCard key={dossier.slug} dossier={dossier} onNavigate={onNavigate} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

function FocusDetailPage({
  slug,
  onNavigate,
}: {
  slug?: string;
  onNavigate: (href: string) => void;
}) {
  const dossier = focus.find((item) => item.slug === slug);

  if (!dossier) return <NotFoundPage onNavigate={onNavigate} />;

  const presseFrancaise = dossier.sources.filter((source) => source.zone === "France");
  const presseEtrangere = dossier.sources.filter((source) => source.zone === "International");

  return (
    <article className="article-page">
      <div className="container article-head">
        <nav className="breadcrumb" aria-label="Fil d'Ariane">
          <SmartLink href="/" onNavigate={onNavigate}>
            Accueil
          </SmartLink>
          <ChevronRight size={15} aria-hidden="true" />
          <SmartLink href="/focus" onNavigate={onNavigate}>
            Focus
          </SmartLink>
        </nav>
        <p className="kicker">Focus</p>
        <h1>{dossier.title}</h1>
        <p className="article-dek">{dossier.chapeau}</p>
        <div className="article-meta">
          <time dateTime={dossier.dateIso}>{dossier.date}</time>
        </div>
      </div>
      <div className="article-body">
        <aside className="focus-idea-block">
          <p className="focus-idea-label">L'idée de Dominique de Villepin</p>
          <blockquote>{dossier.idea}</blockquote>
        </aside>
        {dossier.corps.map((paragraph, index) => (
          <p key={paragraph} className={index === 0 ? "dropcap" : undefined}>
            {paragraph}
          </p>
        ))}
        <aside className="actualite-sources" aria-label="Sources">
          <h2>Presse française</h2>
          <ul>
            {presseFrancaise.map((source) => (
              <li key={source.url}>
                <a href={source.url} target="_blank" rel="noopener">
                  {source.label}
                </a>
              </li>
            ))}
          </ul>
          <h2>Presse étrangère</h2>
          <ul>
            {presseEtrangere.map((source) => (
              <li key={source.url}>
                <a href={source.url} target="_blank" rel="noopener">
                  {source.label}
                </a>
              </li>
            ))}
          </ul>
        </aside>
        <p className="actualite-back">
          <SmartLink href="/focus" className="text-link" onNavigate={onNavigate}>
            Tous les dossiers
            <ArrowRight size={16} aria-hidden="true" />
          </SmartLink>
        </p>
      </div>
    </article>
  );
}

function VideosPage({ onNavigate }: { onNavigate: (href: string) => void }) {
  const [kind, setKind] = useState<(typeof videoKinds)[number]>("Tout");
  const kindsDisponibles = videoKinds.filter(
    (item) => item === "Tout" || videos.some((video) => video.kind === item),
  );
  const visibleVideos = kind === "Tout" ? videos : videos.filter((video) => video.kind === kind);

  return (
    <>
      <PageIntro
        eyebrow="Capsules vidéo"
        title="Entretiens, capsules et discours"
        text="Une bibliothèque courte pour suivre les prises de parole, les formats pédagogiques et les débats."
      />
      <section className="section section-tight">
        <div className="container">
          {kindsDisponibles.length > 2 ? (
            <SegmentedControl
              label="Filtrer les capsules"
              values={kindsDisponibles}
              value={kind}
              onChange={setKind}
            />
          ) : null}
          <div className="video-grid">
            {visibleVideos.map((video) => (
              <VideoCard key={`${video.title}-${video.date}`} video={video} />
            ))}
          </div>
          <SmartLink href="/articles" className="wide-link" onNavigate={onNavigate}>
            <span>
              <Newspaper size={20} aria-hidden="true" />
              Lire les réflexions liées aux capsules
            </span>
            <ArrowRight size={18} aria-hidden="true" />
          </SmartLink>
        </div>
      </section>
    </>
  );
}

function VideoCard({ video }: { video: (typeof videos)[number] }) {
  const contenu = (
    <>
      <div className={`video-thumb visual-${video.theme.toLowerCase()}`}>
        <span className="play-button small" aria-hidden="true">
          <Play size={18} fill="currentColor" />
        </span>
        <time>{video.duration}</time>
      </div>
      <div className="video-card-body">
        <div className="meta-row">
          <span>{video.kind}</span>
          <span>{video.platform}</span>
        </div>
        <h3>{video.title}</h3>
        <p>{video.date}</p>
        {video.source ? <p className="video-source">{video.source}</p> : null}
      </div>
    </>
  );

  if (video.url) {
    return (
      <a className="video-card" href={video.url} target="_blank" rel="noopener">
        {contenu}
      </a>
    );
  }

  return <article className="video-card">{contenu}</article>;
}

function AboutPage({ onNavigate }: { onNavigate: (href: string) => void }) {
  return (
    <>
      <PageIntro
        eyebrow="Qui sommes-nous"
        title="Un collectif à visage découvert"
        text="Des enseignants, artisans, étudiants, retraités et cadres réunis par une conviction commune: cette voix mérite d'être entendue et débattue."
      />
      <section className="section section-tight">
        <div className="container values-grid">
          {values.map((value) => (
            <article key={value.title} className="value-card">
              <ShieldCheck size={24} aria-hidden="true" />
              <h2>{value.title}</h2>
              <p>{value.text}</p>
            </article>
          ))}
        </div>
      </section>
      <section className="section section-alt" id="contact">
        <div className="container contact-grid">
          <div className="contact-panel dark">
            <p className="eyebrow light">Contact</p>
            <h2>Écrivez-nous</h2>
            <p>
              Pour rejoindre le collectif, proposer une tribune ou organiser une rencontre près de chez
              vous.
            </p>
            <a href={`mailto:${SITE.email}`} className="btn btn-light">
              <Mail size={18} aria-hidden="true" />
              {SITE.email}
            </a>
          </div>
          <div className="contact-panel">
            <p className="eyebrow">Presse et médias</p>
            <h2>Journalistes</h2>
            <p>
              Dossier de presse, demandes d'interview et vérification des publications: même adresse,
              objet "Presse". Réponse annoncée sous 48 h.
            </p>
            <SmartLink href="/articles" className="btn btn-ghost" onNavigate={onNavigate}>
              <Newspaper size={18} aria-hidden="true" />
              Nos publications
            </SmartLink>
          </div>
        </div>
      </section>
    </>
  );
}

function LegalPage() {
  return (
    <StaticPage
      title="Mentions légales"
      eyebrow="Cadre éditorial"
      paragraphs={[
        SITE.disclaimer,
        "Responsable de publication: collectif éditorial Ma voix pour de Villepin. Contact: contact@mavoixpourvillepin.fr.",
        "Les textes publiés engagent leurs auteurs. Les crédits photographiques et sources externes sont indiqués au plus près des contenus concernés.",
      ]}
    />
  );
}

function PrivacyPage() {
  return (
    <StaticPage
      title="Politique de confidentialité"
      eyebrow="Données personnelles"
      paragraphs={[
        "Le site limite la collecte de données au strict nécessaire: les échanges par e-mail sont traités uniquement pour répondre aux demandes reçues.",
        "Aucun formulaire de don, d'adhésion ou de prospection automatisée n'est intégré dans cette version.",
        "Pour toute demande relative à vos données, écrivez à contact@mavoixpourvillepin.fr.",
      ]}
    />
  );
}

function StaticPage({
  title,
  eyebrow,
  paragraphs,
}: {
  title: string;
  eyebrow: string;
  paragraphs: string[];
}) {
  return (
    <>
      <PageIntro eyebrow={eyebrow} title={title} text={paragraphs[0]} />
      <section className="section section-tight">
        <div className="container text-page">
          {paragraphs.slice(1).map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
        </div>
      </section>
    </>
  );
}

function NotFoundPage({ onNavigate }: { onNavigate: (href: string) => void }) {
  return (
    <section className="not-found">
      <div className="container">
        <p className="eyebrow">404</p>
        <h1>Page introuvable</h1>
        <p>La page demandée n'existe pas ou a changé d'adresse.</p>
        <SmartLink href="/" className="btn btn-primary" onNavigate={onNavigate}>
          <ArrowRight size={18} aria-hidden="true" />
          Retour à l'accueil
        </SmartLink>
      </div>
    </section>
  );
}

function PageIntro({ eyebrow, title, text }: { eyebrow: string; title: string; text: string }) {
  return (
    <header className="page-intro">
      <div className="container">
        <p className="eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        <p>{text}</p>
      </div>
    </header>
  );
}

function SegmentedControl<T extends string>({
  label,
  values,
  value,
  onChange,
}: {
  label: string;
  values: readonly T[];
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <div className="segmented" role="group" aria-label={label}>
      {values.map((item) => (
        <button
          key={item}
          type="button"
          className={item === value ? "selected" : ""}
          aria-pressed={item === value}
          onClick={() => onChange(item)}
        >
          {item === "Tout" ? "Tout" : item}
        </button>
      ))}
    </div>
  );
}

function Footer({ onNavigate }: { onNavigate: (href: string) => void }) {
  return (
    <footer className="site-footer">
      <div className="container footer-grid">
        <div>
          <div className="footer-brand">Ma voix pour de Villepin</div>
          <p>{SITE.disclaimer}</p>
        </div>
        <div>
          <h2>Navigation</h2>
          <ul>
            {navItems.slice(1).map((item) => (
              <li key={item.href}>
                <SmartLink href={item.href} onNavigate={onNavigate}>
                  {item.label}
                </SmartLink>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h2>Contact</h2>
          <ul>
            <li>
              <a href={`mailto:${SITE.email}`}>{SITE.email}</a>
            </li>
            <li>
              <SmartLink href="/mentions-legales" onNavigate={onNavigate}>
                Mentions légales
              </SmartLink>
            </li>
            <li>
              <SmartLink href="/confidentialite" onNavigate={onNavigate}>
                Confidentialité
              </SmartLink>
            </li>
          </ul>
        </div>
      </div>
      <div className="container footer-bottom">
        <span>© 2026 {SITE.name}</span>
        <span>Portrait: Service photographique de Matignon, Licence Ouverte.</span>
      </div>
    </footer>
  );
}